import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { PlayCircle, Repeat } from "lucide-react";
import { API } from "@/lib/api";
import { faceplateClass } from "@/lib/theme";

const src = (v) => `${API}/videos/${v.id}/file`;

export const VideoSection = ({ loopDefault, faceplate }) => {
  const [loop, setLoop] = useState(loopDefault);
  useEffect(() => setLoop(loopDefault), [loopDefault]);
  const [videos, setVideos] = useState([]);
  const [current, setCurrent] = useState(null);
  const [failed, setFailed] = useState(null);
  const player = useRef(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/videos`).then(({ data }) => {
      setVideos(data);
      setCurrent((c) => (c && data.some((v) => v.id === c.id) ? c : data[0] || null));
    }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);


  const pick = (v) => {
    setCurrent(v);
    setTimeout(() => player.current?.play().catch(() => {}), 50);
  };

  if (!videos.length) return null;
  return (
    <section className="rise max-w-4xl mx-auto w-full" data-testid="video-section">
      <h3 className="section-title">~ videos ~</h3>
      <div className={`hardware car-deck ${faceplateClass(faceplate)} rounded-2xl p-3`} data-faceplate={faceplate || "custom"} data-testid="video-faceplate">
        <div className="lcd rounded-xl overflow-hidden relative">
          {failed === current?.id && (
            <div className="absolute inset-0 z-10 flex items-center justify-center text-center p-6 lcd-text text-sm" data-testid="video-error">
              This video can't play in this browser. Try another video or browser.
            </div>
          )}
          <video key={current?.id} ref={player} src={current ? src(current) : undefined} controls playsInline preload="metadata" loop={loop}
            onEnded={() => { const i = videos.findIndex((v) => v.id === current?.id); if (videos.length > 1) pick(videos[(i + 1) % videos.length]); }} onError={() => setFailed(current?.id)} className="w-full aspect-video bg-black" data-testid="video-player" />
        </div>
        <div className="mt-3 px-1 flex items-center gap-3">
          <p className="font-bold truncate deck-brand flex-1 not-italic" data-testid="video-now-title">{current?.title}</p>
          <button onClick={() => setLoop(!loop)} className="deck-btn rounded-md h-9 px-3 font-mono text-[0.65rem] tracking-widest flex items-center gap-1.5" aria-pressed={loop} data-testid="video-loop-toggle">
            <span className={`w-1.5 h-1.5 rounded-full ${loop ? "lcd-glow" : ""}`} style={{ background: loop ? "var(--t-label)" : "#3a3a3a" }} />
            <Repeat className="w-3.5 h-3.5" /> LOOP {loop ? "ON" : "OFF"}
          </button>
        </div>
      </div>
      {videos.length > 1 && (
        <ol className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4" data-testid="video-playlist">
          {videos.map((v, i) => (
            <li key={v.id}>
              <button onClick={() => pick(v)} className={`dash-card w-full text-left p-2 ${current?.id === v.id ? "!border-solid" : ""}`} data-testid={`video-item-${i}`}>
                <div className="relative rounded overflow-hidden bg-black aspect-video">
                  <video src={`${src(v)}#t=1`} preload="metadata" muted playsInline className="w-full h-full object-cover pointer-events-none" />
                  <PlayCircle className="absolute inset-0 m-auto w-8 h-8 hw-icon" style={{ fill: "transparent" }} />
                </div>
                <p className="text-xs font-bold mt-2 truncate" style={{ color: "var(--t-label)" }}>{v.title}</p>
              </button>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};
